import React, { useMemo } from "react";
import {
  useAddLineMutation,
  useDeleteLineMutation,
  useGetLinesQuery,
  useUpdateLineMutation,
} from "../../features/lines/lineApi";
import { useLazyPlantOptions } from "../../features/plants/usePlantOptions";
import MasterCrudPage from "../masters/MasterCrudPage";
import { withAuditFilters } from "../masters/filterMetadata";
import { getPlantLabel, renderPlantCell } from "../masters/masterMetadata";

const getLineQrValue = (row) => row?.qr_value || row?.line_id;
const getLineQrDisplayValue = (row) => row?.qr_display_text || row?.line_id;
const lineFilterFields = withAuditFilters([
  { name: "line_id", label: "Line ID", optionSource: "lines" },
  { name: "line_name", label: "Line Name" },
  { name: "line_code", label: "Line Code" },
]);

const Line = () => {
  const {
    options: plantOptions,
    isFetching: isLoadingPlants,
    loadOptions: loadPlantOptions,
  } = useLazyPlantOptions();

  const fields = useMemo(
    () => [
      {
        name: "plant_id",
        label: "Plant",
        type: "select",
        options: plantOptions,
        placeholder: isLoadingPlants ? "Loading plants..." : "Select plant",
        disabled: isLoadingPlants && plantOptions.length === 0,
        disabledOnEdit: true,
        required: true,
        fullWidth: true,
      },
      {
        name: "line_name",
        label: "Line Name",
        placeholder: "Enter line name",
        required: true,
      },
      {
        name: "line_code",
        label: "Line Code",
        placeholder: "Enter line code",
        required: true,
      },
    ],
    [isLoadingPlants, plantOptions]
  );

  const columns = useMemo(
    () => (metaData) => [
      {
        name: "Line ID",
        selector: (row) => row?.line_id || "-",
        sortable: true,
      },
      {
        name: "Line Name",
        selector: (row) => row?.line_name || "-",
        sortable: true,
        grow: 2,
      },
      {
        name: "Line Code",
        selector: (row) => row?.line_code || "-",
        sortable: true,
      },
      {
        name: "Plant",
        selector: (row) => getPlantLabel(row, metaData),
        cell: (row) => renderPlantCell(row, metaData),
        sortable: true,
        grow: 2,
      },
    ],
    []
  );

  return (
    <MasterCrudPage
      entityName="Line"
      pageTitle="Lines"
      idField="line_id"
      displayField="line_name"
      fields={fields}
      columns={columns}
      searchFields={[
        "line_id",
        "line_name",
        "line_code",
        "plant_id",
        (row, metaData) => getPlantLabel(row, metaData),
      ]}
      searchPlaceholder="Search lines"
      useListQuery={useGetLinesQuery}
      useCreateMutation={useAddLineMutation}
      useUpdateMutation={useUpdateLineMutation}
      useDeleteMutation={useDeleteLineMutation}
      filterFields={lineFilterFields}
      qrDisplayValue={getLineQrDisplayValue}
      qrValue={getLineQrValue}
      transformPayload={(payload, editData) => {
        if (!editData) return payload;
        const updatePayload = { ...payload };
        delete updatePayload.plant_id;
        return updatePayload;
      }}
      onOpenForm={loadPlantOptions}
      noDataLabel="No lines found"
    />
  );
};

export default Line;
